import type { TracerProvider } from '@opentelemetry/api';
import { type z } from 'zod';
import { AxiomCLIError } from '../util/errors';
import { getAuthContext } from '../cli/auth';

/**
 * Utility type to make all properties in T required recursively.
 * Keeps the types as-is but removes the optionality.
 */
type DeepRequired<T> =
  T extends Array<infer U>
    ? Array<U>
    : T extends (...args: any[]) => any
      ? T
      : T extends object
        ? {
            [P in keyof T]-?: DeepRequired<T[P]>;
          }
        : T;

/**
 * Axiom API connection configuration
 */
export interface AxiomConnectionConfig {
  /**
   * Axiom API URL
   * @default 'https://api.axiom.co'
   * @example 'https://api.axiom.co'
   */
  url?: string;

  /**
   * Axiom Edge URL for ingest and query operations.
   * When set, this URL is used for sending traces and querying data,
   * while the regular `url` is used for API/console operations.
   * Falls back to `url` if not specified.
   * @example 'https://eu-central-1.aws.edge.axiom.co'
   */
  edgeUrl?: string;

  /**
   * Axiom API token (can be undefined if not set)
   * @example process.env.AXIOM_TOKEN
   */
  token?: string | undefined;

  /**
   * Axiom dataset name
   * @example process.env.AXIOM_DATASET
   */
  dataset?: string;

  /**
   * Axiom organization ID
   * @example process.env.AXIOM_ORG_ID
   */
  orgId?: string;
}

/**
 * Options passed to the instrumentation hook
 * - url: string
 * - token: string
 * - dataset: string
 */
export interface AxiomEvalInstrumentationOptions {
  url: string;
  edgeUrl: string;
  token: string;
  dataset: string;
  orgId?: string;
}

/**
 * Result returned from the instrumentation hook
 */
export interface AxiomEvalInstrumentationResult {
  /**
   * TracerProvider to be flushed when eval finishes.
   *
   * If you use the NodeSDK or register your provider globally, you don't need to return it
   * here as the NodeSDK automatically flushes the global provider.
   *
   * Only return a provider if you want Axiom to explicitly flush it for you and it's not
   * registered as the global tracer provider.
   */
  provider?: TracerProvider;
}

export type SyncAxiomEvalInstrumentationHook = (
  options: AxiomEvalInstrumentationOptions,
) => AxiomEvalInstrumentationResult;

export type AsyncAxiomEvalInstrumentationHook = (
  options: AxiomEvalInstrumentationOptions,
) => Promise<AxiomEvalInstrumentationResult>;

/**
 * Hook function to initialize application OpenTelemetry instrumentation.
 * Called before eval execution with resolved Axiom connection details.
 *
 * @param options - Configuration options
 * @param options.url - Axiom API URL
 * @param options.token - Axiom API token
 * @param options.dataset - Axiom dataset name
 * @returns TracerProvider or Promise resolving to TracerProvider
 *
 * @example
 * ```typescript
 * instrumentation: ({ url, token, dataset }) => {
 *   return setupAppInstrumentation({ url, token, dataset });
 * }
 * ```
 */
export type AxiomEvalInstrumentationHook = (
  options: AxiomEvalInstrumentationOptions,
) => AxiomEvalInstrumentationResult | Promise<AxiomEvalInstrumentationResult>;

/**
 * Axiom AI SDK base configuration (user-facing, all optional)
 */
export const DEFAULT_EVAL_INCLUDE = ['**/*.eval.{ts,js,mts,mjs,cts,cjs}'] as const;

export interface AxiomConfigBase {
  /**
   * Eval configuration settings
   *
   * @example
   * ```typescript
   * eval: {
   *   url: process.env.AXIOM_URL,
   *   token: process.env.AXIOM_TOKEN,
   *   dataset: process.env.AXIOM_DATASET
   * }
   * ```
   */
  eval?: AxiomConnectionConfig & {
    /**
     * Zod schema for flag validation.
     * When provided, CLI flags (--flag.*) are validated against this schema
     * before eval execution begins.
     *
     * @example
     * ```typescript
     * import { z } from 'zod';
     *
     * export default defineConfig({
     *   eval: {
     *     flagSchema: z.object({
     *       model: z.object({
     *         temperature: z.number().min(0).max(2).default(0.7),
     *         name: z.string().default('gpt-4o'),
     *       }),
     *     }),
     *   }
     * });
     * ```
     */
    flagSchema?: z.ZodObject<any> | null;
    /**
     * Optional hook to initialize application OpenTelemetry instrumentation.
     * Called before eval execution with resolved Axiom connection details.
     * Return your configured tracer provider/tracer (or void) after registering them.
     */
    instrumentation?: AxiomEvalInstrumentationHook | null;
    /**
     * Timeout for eval execution in milliseconds
     * @default 60000
     */
    timeoutMs?: number;
    /**
     * Glob patterns to include when running evals
     * @default ['**\/*.eval.{ts,js,mts,mjs,cts,cjs}']
     * @example ['**\/*.eval.ts', 'tests/**\/*.test.ts']
     */
    include?: string[];
    /**
     * Glob patterns to exclude when running evals
     * @default ['**\/node_modules/**', '**\/dist/**', '**\/build/**']
     * @example ['**\/node_modules/**', '**\/.next/**']
     */
    exclude?: string[];
    /**
     * Optional Vitest config file to load for eval runs.
     *
     * When omitted or set to false, evals run without loading any Vitest config,
     * keeping them isolated from the application's Vite/Vitest plugin graph.
     *
     * @default false
     * @example './vitest.eval.config.ts'
     */
    vitestConfig?: string | false;
  };
}

/**
 * Resolved Axiom AI SDK configuration with all required keys.
 * This is the type returned after merging user config with defaults.
 *
 * Uses DeepRequired to ensure all optional properties from AxiomConfigBase
 * become required, preventing missing properties in the resolved config.
 */
export type ResolvedAxiomConfig = DeepRequired<AxiomConfigBase>;

/**
 * Axiom AI SDK configuration with optional environment-specific overrides.
 *
 * Supports c12 environment overrides using $development, $production, etc.
 *
 * @example
 * ```typescript
 * export default defineConfig({
 *   eval: {
 *     url: process.env.AXIOM_URL,
 *     token: process.env.AXIOM_TOKEN,
 *     dataset: process.env.AXIOM_DATASET,
 *   },
 * })
 * ```
 */
export interface AxiomConfig extends AxiomConfigBase {
  /**
   * Allow c12 environment-specific overrides ($development, $production, $test etc.)
   * but don't show them in autocomplete for now
   */
  [key: `$${string}`]: Partial<AxiomConfigBase> | undefined;
}

/**
 * Type-safe helper for defining Axiom configuration.
 *
 * @param config - The configuration object
 * @returns The same configuration object with type checking
 *
 * @example
 * ```typescript
 * import { defineConfig } from 'axiom/ai/config';
 *
 * export default defineConfig({
 *   eval: {
 *     url: process.env.AXIOM_URL,
 *     token: process.env.AXIOM_TOKEN,
 *     dataset: process.env.AXIOM_DATASET,
 *     include: ['**\/*.eval.{ts,js}'],
 *     instrumentation: ({ url, token, dataset }) => setupAppInstrumentation({ url, token, dataset }),
 *   },
 * });
 * ```
 */
export function defineConfig(config: AxiomConfig): AxiomConfig {
  return config;
}

/**
 * Create partial default configuration from auth context or environment variables.
 * Does not throw if required values are missing - validation happens after merge.
 *
 * @returns Partial configuration with defaults and auth/env var values
 * @internal
 */
export function createPartialDefaults(): Partial<AxiomConfigBase> {
  // Try to get auth context first (available when running via CLI)
  // Fall back to process.env for backward compatibility (e.g., when used outside CLI)
  let token: string | undefined;
  let url: string | undefined;
  let orgId: string | undefined;

  try {
    const authContext = getAuthContext();
    if (authContext) {
      token = authContext.token;
      url = authContext.url;
      orgId = authContext.orgId;
    }
  } catch {
    // Auth context not available, fall back to env vars
  }

  // Fall back to process.env if auth context not available
  token = token || process.env.AXIOM_TOKEN;
  url = url || process.env.AXIOM_URL;
  orgId = orgId || process.env.AXIOM_ORG_ID;

  // Edge URL for ingest and query operations
  const edgeUrl = process.env.AXIOM_EDGE_URL;

  return {
    eval: {
      url: url || 'https://api.axiom.co',
      edgeUrl: edgeUrl,
      orgId,
      token,
      dataset: process.env.AXIOM_DATASET,
      flagSchema: undefined,
      instrumentation: null,
      include: [...DEFAULT_EVAL_INCLUDE],
      exclude: [],
      timeoutMs: 60_000,
      vitestConfig: false,
    },
  };
}

/**
 * Validates and returns a fully resolved Axiom configuration.
 *
 * @param config - Partial configuration to validate
 * @returns Fully resolved configuration with all required fields
 * @throws {AxiomCLIError} If required fields are missing or invalid
 * @internal
 */
export function validateConfig(config: Partial<AxiomConfigBase>): ResolvedAxiomConfig {
  const errors: string[] = [];
  const isDebug = process.env.AXIOM_DEBUG === 'true';

  if (!isDebug) {
    if (!config.eval?.token) {
      errors.push(
        'eval.token is required (set in axiom.config.ts or AXIOM_TOKEN environment variable)',
      );
    }
    if (!config.eval?.dataset) {
      errors.push(
        'eval.dataset is required (set in axiom.config.ts or AXIOM_DATASET environment variable)',
      );
    }

    if (!config.eval?.url) {
      console.log(
        'eval.url was not specified. Defaulting to `https://api.axiom.co`. Please set it in axiom.config.ts or AXIOM_URL environment variable if you want to use a different endpoint.',
      );
    }
  }

  const instrumentation = config.eval?.instrumentation;
  if (
    instrumentation !== null &&
    instrumentation !== undefined &&
    typeof instrumentation !== 'function'
  ) {
    errors.push(
      'eval.instrumentation must be a function returning OTEL setup information or null.',
    );
  }

  const vitestConfig = config.eval?.vitestConfig;
  if (vitestConfig !== undefined && vitestConfig !== false && typeof vitestConfig !== 'string') {
    errors.push('eval.vitestConfig must be a string path or false.');
  }

  if (errors.length > 0) {
    throw new AxiomCLIError(`Invalid Axiom configuration:\n  - ${errors.join('\n  - ')}`);
  }

  return config as ResolvedAxiomConfig;
}
